/**
 * UPT-2079 L6b (web §5.5, §5.8) — phrase traduite dont certains paramètres sont rendus à part : des valeurs TECHNIQUES en `<code dir="ltr">` (noms de
 * table et de colonne, identifiants, empreinte courte : jamais traduits, de gauche à droite même dans une page arabe) et des NOMBRES en `<NumericValue>`
 * (`<bdi dir="ltr">`, chiffres tabulaires : « 8 lignes » reste lisible dans une phrase RTL). Même principe que `PreviewMessage` : chaque valeur technique
 * est remplacée dans le message par un repère (caractères de l'usage privé), puis le texte est découpé autour des repères. AUCUN HTML n'est interprété,
 * aucune valeur n'est transformée en lien.
 *
 * Relecture du 18.10 (W7) : les nombres sont passés tels quels à `formatMessage` (un NOMBRE, jamais un texte) — le gabarit choisit sa forme
 * (`{count, plural, one {…} other {…}}`) et formate `#` / `{n}` selon la langue. Un gabarit isole ses chiffres avec la balise `<num>…</num>`
 * (rendue en `<NumericValue>`) ; sans la balise, le nombre est un texte ordinaire de la phrase.
 */
import { Children, Fragment, type ReactNode } from 'react';
import { useIntl } from '@/react-intl';
import { NumericValue } from '@/uptk/core/components/ui/NumericValue';

const MARK_OPEN = '';
const MARK_CLOSE = '';
const MARK_PATTERN = /(\d+)/;

export interface PipelineTextProps {
  /** Clé de traduction (issue d'un accesseur de `pipelineI18n`, ou littérale chez l'appelant). */
  id: string;
  /** Paramètres rendus en texte. */
  values?: Record<string, string | number>;
  /** Paramètres rendus en `<code dir="ltr">` ; une liste est jointe par « , ». */
  codes?: Record<string, string | readonly string[]>;
  /** Paramètres NUMÉRIQUES : passés en nombre au gabarit (pluriel), isolés par `<num>` quand le gabarit le demande. */
  numbers?: Record<string, number>;
}

/** Découpe un texte autour des repères des valeurs techniques : texte ordinaire ou `<code dir="ltr">`. */
function renderCodes(text: string, codes: readonly string[], keyPrefix: string): ReactNode[] {
  return text.split(MARK_PATTERN).map((part, index) => {
    if (index % 2 === 0) {
      return <Fragment key={`${keyPrefix}-${index}`}>{part}</Fragment>;
    }
    const code = codes[Number(part)];
    if (code === undefined) return null;
    return (
      <code key={`${keyPrefix}-${index}`} dir="ltr" className="break-all">
        {code}
      </code>
    );
  });
}

export function PipelineText({ id, values, codes, numbers }: PipelineTextProps) {
  const intl = useIntl();
  const codeTexts: string[] = [];
  const markers: Record<string, string> = {};
  const mark = (text: string): string => {
    codeTexts.push(text);
    return `${MARK_OPEN}${codeTexts.length - 1}${MARK_CLOSE}`;
  };
  Object.entries(codes ?? {}).forEach(([name, value]) => {
    const list: readonly string[] = typeof value === 'string' ? [value] : value;
    markers[name] = list.map((entry) => mark(entry)).join(', ');
  });
  const rendered = intl.formatMessage(
    { id },
    {
      ...(values ?? {}),
      ...(numbers ?? {}),
      ...markers,
      num: (chunks: ReactNode[]) => <NumericValue>{chunks}</NumericValue>,
    }
  );
  const nodes = Children.toArray(Array.isArray(rendered) ? rendered : [rendered]);
  return (
    <>
      {nodes.map((node, index) =>
        typeof node === 'string' ? renderCodes(node, codeTexts, `t${index}`) : <Fragment key={`n${index}`}>{node}</Fragment>
      )}
    </>
  );
}

export default PipelineText;
